import React from 'react';
import dayjs from 'dayjs';
import { Box, Tooltip, Typography } from '@mui/material';
import {
  CHALLENGE_TYPE_LABELS,
  formatChallengeValue,
} from '../../../utils/challengeFormat';
import { partitionLabel } from '../../../utils/incentiveChallengeApi';

/** A target group's range, read off its two figures. */
const bandHeading = (band) => {
  const money = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
  const from = band.from ?? band.revenue_target_from ?? null;
  const to = band.to ?? band.revenue_target_to ?? null;
  if ((from ?? '') === '' && (to ?? '') === '') return 'Everyone';
  if ((from ?? '') === '') return `Up to ${money(to)}`;
  if ((to ?? '') === '') return `${money(from)} and above`;
  return `${money(from)} – ${money(to)}`;
};

const formatRange = (row) =>
  `${dayjs(row.startDate).format('DD MMM YYYY')} – ${dayjs(row.endDate).format('DD MMM YYYY')}`;

export const challengeTableHeader = (renderStatus, renderAchievers, renderActions) => [
  { field: 'name', headerName: 'Name', width: 230, sortable: true },
  {
    field: 'type',
    headerName: 'Type',
    width: 140,
    sortable: true,
    valueGetter: ({ value }) => CHALLENGE_TYPE_LABELS[value] || value,
  },
  { field: 'scopeLabel', headerName: 'Scope', width: 130, sortable: true },
  { field: 'assignmentLabel', headerName: 'Assignment', width: 240, sortable: true },
  {
    field: 'duration',
    headerName: 'Duration',
    width: 220,
    valueGetter: ({ row }) => formatRange(row),
    // A challenge judged round by round says so under its dates — the rounds
    // are what everything else on the row is counted in.
    renderCell: ({ row, value }) => {
      const rounds = partitionLabel(row.partitionType, row.partitionDays, row.partition?.total_rounds);
      if (!rounds) return value;
      return (
        <Box sx={{ lineHeight: 1.25 }}>
          <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {rounds}
            {row.status === 'active' && row.partition
              ? ` · round ${row.partition.elapsed_rounds}`
              : ''}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: 'targets',
    headerName: 'Target',
    width: 150,
    align: 'right',
    sortable: true,
    // A challenge is a ladder now, so the column shows the rungs. One rung is
    // the common case and reads exactly as the old single target did; more than
    // two would not fit, so the rest are behind a hover.
    valueGetter: ({ row }) => {
      const rungs = (row.bands || []).flatMap((b) => b.tiers || []);
      if (!rungs.length) return '—';
      const shown = rungs
        .slice(0, 2)
        .map((t) => formatChallengeValue(t.target, row.type))
        .join(', ');
      return rungs.length > 2 ? `${shown} +${rungs.length - 2}` : shown;
    },
    renderCell: ({ row, value }) => {
      const groups = row.bands || [];
      const rungs = groups.flatMap((b) => b.tiers || []);
      if (!rungs.length) return value;

      // The whole ladder on hover: every rung, what it pays, and which target
      // group it belongs to when there is more than one.
      const title = (
        <Box sx={{ py: 0.5 }}>
          {groups.map((band, i) => (
            <Box key={i} sx={{ mb: i < groups.length - 1 ? 1 : 0 }}>
              {groups.length > 1 && (
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  {bandHeading(band)}
                </Typography>
              )}
              {(band.tiers || []).map((t) => (
                <Box
                  key={t.tier_no ?? t.target}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
                >
                  <Typography variant="caption">
                    {formatChallengeValue(t.target, row.type)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    {t.prize}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      );

      return (
        <Tooltip title={title} arrow placement="left">
          <Box component="span" sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'divider' }}>
            {value}
          </Box>
        </Tooltip>
      );
    },
  },
  {
    // A role challenge counts how many people cleared their own target. A group
    // challenge has one pooled target, so the only honest answer is whether the
    // group reached it — "8 / 10" there would read as eight individual winners.
    field: 'achievers',
    headerName: 'Achievers',
    width: 140,
    renderCell: renderAchievers,
  },
  { field: 'status', headerName: 'Status', width: 130, renderCell: renderStatus },
  { field: 'actions', headerName: '', width: 180, renderCell: renderActions },
];
